import React, { ChangeEvent, LegacyRef, useRef, useState, useMemo } from "react";

import { CircularProgress, Grid } from "@mui/material";
import { Stack } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { format } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { FiDownload, FiEye, FiFile, FiTrash2 } from "react-icons/fi";
import { IconBaseProps } from "react-icons/lib";
import { VscFilePdf } from "react-icons/vsc";

import { useAppState } from "../../../../../context/StateContext";
import { DocumentReference, DocumentReferenceOptions } from "../../../../../models/document";
import { Archive, ShipmentDocument } from "../../../../../models/shipment.document";
import { User } from "../../../../../models/user";
import { downloadAndGeneratePdf } from "../../../../../util/downloadAndGeneratePdf";
import { downloadFile } from "../../../../../util/downloadFile";
import { getUserFullname } from "../../../../../util/names";
import { NotificationToast } from "../../../../Generic/Toast/NotificationToast";
import { DefaultTooltip } from "../../../../Generic/Tooltip";
import DeleteDocumentModal, {
  DeleteDocumentConfirmationModalHandles,
} from "./components/DeleteDocumentModal";
import { BadgeType, DocumentsBadge } from "./components/DocumentsBadge";
import { TextButton } from "./components/TextButton";
import useStyles, { BottomButtonContainer, DeleteDocumentIcon } from "./styles";

interface Props {
  shipmentDocument: ShipmentDocument;
  onUploadDocument?: (url: string) => void;
  onClearDocument: (shipmentDocumentToClear: ShipmentDocument) => void;
  onDeleteDocument: (id: number) => void;
  onRemoveArchive?: (archiveId: number) => void;
  loadingUpload?: boolean;
  loadingClearDocument?: boolean;
  loadingRemoveArchive?: boolean;
  disabledUploadButton?: boolean;
  onDownloadDocument?: (
    documentId: number,
    documentType?: DocumentReference,
    onFinally?: () => void
  ) => void;
  onDownloadDocumentCsv?: (
    documentId: number,
    documentType?: DocumentReference,
    onFinally?: () => void
  ) => void;
  openedFromView?: boolean;
  viewOnly?: boolean;
  isBooking?: boolean;
}

const MAX_SIZE = 2 * 1024 * 1024;

const DocumentsItem = (props: Props) => {
  const {
    onUploadDocument,
    onDownloadDocument,
    onDownloadDocumentCsv,
    onClearDocument,
    onDeleteDocument,
    onRemoveArchive,
    loadingUpload,
    shipmentDocument,
    disabledUploadButton,
    loadingClearDocument,
    loadingRemoveArchive,
    isBooking,
    openedFromView,
    viewOnly,
  } = props;

  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const deleteModalRef = useRef<DeleteDocumentConfirmationModalHandles>(null);
  const MustBeConfirmedString = useMemo(
    () => translate(`${isBooking ? "booking" : "shipment"}MustBeConfirmed`),
    [isBooking, translate]
  );
  const isSSCCUploadedDocument = (document: ShipmentDocument) => {
    // eslint-disable-next-line prefer-regex-literals
    const regex = new RegExp("^SSCC CSV uploaded on*");

    return regex.test(document.visualName ?? document.name);
  };
  const canFileBeDeleted = useMemo(
    () =>
      !(
        isSSCCUploadedDocument(shipmentDocument) ||
        shipmentDocument.required ||
        shipmentDocument.generated
      ) &&
      !openedFromView &&
      !viewOnly,
    [shipmentDocument]
  );

  const archives = useMemo<Archive[]>(
    () => shipmentDocument?.archives ?? [],
    [shipmentDocument?.archives]
  );

  const isThereAFile = useMemo(
    () =>
      (shipmentDocument?.uploaded &&
        shipmentDocument?.uploadedAt &&
        (archives.length > 0 || shipmentDocument?.file)) ||
      (shipmentDocument.generated && onDownloadDocument),
    [shipmentDocument, onDownloadDocument, archives]
  );

  const canFileBeCleared = useMemo(
    () =>
      !openedFromView &&
      !viewOnly &&
      !(isSSCCUploadedDocument(shipmentDocument) || shipmentDocument.generated) &&
      isThereAFile,
    [shipmentDocument]
  );

  const buttons = {
    viewFile: {
      text: translate("viewFile"),
      renderIcon: (props?: IconBaseProps) => <FiEye {...props} />,
      textLoading: `${
        shipmentDocument.generated ? translate("generatingFile") : translate("openingFile")
      }...`,
    },
    upload: {
      text: translate("upload"),
      color: colors.brandBlue500,
      renderIcon: (props?: IconBaseProps) => <FiFile {...props} />,
      textLoading: `${translate("uploading")}...`,
    },
    clearFile: {
      text: translate("clearFile"),
      color: colors.error500,
      renderIcon: (props?: IconBaseProps) => <FiTrash2 {...props} />,
      textLoading: `${translate("clearingFile")}...`,
    },
  };

  const fileInput: LegacyRef<HTMLInputElement> = useRef(null);

  const userUploaded = useMemo(() => {
    if (!shipmentDocument.user) {
      return {
        name: currentUser?.name,
        lastName: currentUser?.lastName,
      } as User;
    }

    return shipmentDocument.user;
  }, [shipmentDocument.user]);

  const chooseFile = () => {
    if (fileInput && fileInput?.current) fileInput?.current?.click();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event?.target && event?.target?.files && event?.target?.files?.length > 0) {
      const file = event?.target?.files[0];

      const splittedFileName = file.name.split(".");
      const extensionFromName = splittedFileName[splittedFileName.length - 1];
      let validSize = true;

      if (file?.size && file?.size > MAX_SIZE) {
        NotificationToast.error(translate("theFileMustBeLessThan2mbInSize"));

        validSize = false;
      }

      if (file && extensionFromName === "pdf" && validSize) {
        const reader = new FileReader();

        reader.addEventListener("load", () => {
          const uri = reader.result?.toString();

          if (uri) {
            onUploadDocument?.(uri);
          }
        });
        reader.readAsDataURL(file);
      }
    }
    event.target.value = "";
  };

  const renderButtonUpload = () => {
    if (shipmentDocument.generated) {
      return (
        <>
          <DefaultTooltip title={!onDownloadDocument ? MustBeConfirmedString : ""}>
            <div>
              <TextButton
                {...buttons.viewFile}
                onClick={() => downloadDocumentAndViewFile(shipmentDocument.file ?? "")}
                disabled={true}
              />
            </div>
          </DefaultTooltip>
          {isCiplDocument && onDownloadDocumentCsv ? (
            <DefaultTooltip title={!onDownloadDocument ? MustBeConfirmedString : ""}>
              <div>
                <TextButton
                  text={translate("downloadCsv")}
                  renderIcon={(props?: IconBaseProps) => <FiDownload {...props} />}
                  textLoading={`${translate("generatingFile")}...`}
                  onClick={() => {}}
                  disabled={true}
                />
              </div>
            </DefaultTooltip>
          ) : null}
        </>
      );
    }

    return (
      <>
        <DefaultTooltip
          title={`${translate("maxSize")} ${translate("2Mb")}, ${translate("formats")}: ${translate(
            "pdf"
          )}`}
        >
          <div>
            <TextButton
              {...buttons.upload}
              onClick={chooseFile}
              disabled={disabledUploadButton || loadingUpload}
              loading={loadingUpload}
            />
          </div>
        </DefaultTooltip>
        <input
          type="file"
          className={classes.input}
          ref={fileInput}
          value=""
          accept=".pdf"
          onChange={handleChange}
        />
      </>
    );
  };

  const renderTopInfo = () => {
    if (shipmentDocument.generated) {
      if (!onDownloadDocument) {
        return (
          <DefaultTooltip title={MustBeConfirmedString}>
            <div>
              <DocumentsBadge text={translate("notAvailableYet")} type={BadgeType.ERROR} />
            </div>
          </DefaultTooltip>
        );
      }

      return <DocumentsBadge type={BadgeType.SUCCESS} text={translate("generatedBySystem")} />;
    }

    if (shipmentDocument?.uploaded && shipmentDocument.uploadedAt) {
      return (
        <DefaultTooltip
          className={classes.uploadTooltip}
          title={`${translate("uploadMade")} ${translate("on").toLocaleLowerCase()} ${format(
            new Date(shipmentDocument.uploadedAt),
            "dd/MM/yyyy HH:mm a"
          )} ${translate("by")} ${getUserFullname(userUploaded)}`}
        >
          <div>
            <DocumentsBadge type={BadgeType.SUCCESS} text={translate("uploaded")} />
          </div>
        </DefaultTooltip>
      );
    }

    if (shipmentDocument?.required || !shipmentDocument?.file) {
      return (
        <>
          {shipmentDocument?.required && <DocumentsBadge text={translate("mandatory")} />}
          {!shipmentDocument?.file && (
            <DocumentsBadge text={translate("missing")} type={BadgeType.ERROR} />
          )}
        </>
      );
    }

    return null;
  };

  const isCiplDocument =
    shipmentDocument.reference ===
    DocumentReferenceOptions.SHIPMENT.COMMERCIAL_INVOICE_PACKING_LIST;

  const [loadingCsvFile, setLoadingCsvFile] = useState<boolean>(false);
  const [loadingViewFile, setLoadingViewFile] = useState<boolean>(false);
  const downloadDocumentAndViewFile = (urlToDownload: string, onFinally?: () => void) => {
    setLoadingViewFile(true);

    if (shipmentDocument.id && shipmentDocument.generated && onDownloadDocument) {
      onDownloadDocument(shipmentDocument.id, shipmentDocument.reference, () => {
        setLoadingViewFile(false);
        onFinally?.();
      });

      return;
    }

    const onSuccess = () => {
      setLoadingViewFile(false);
      onFinally?.();
    };

    const onFailure = () => {
      setLoadingViewFile(false);
      onFinally?.();
    };

    if (shipmentDocument?.fileType === "csv") {
      downloadFile(urlToDownload, "csv", shipmentDocument.name, onSuccess, onFailure);

      return;
    }

    downloadAndGeneratePdf(urlToDownload, onSuccess, onFailure);
  };

  const [archiveBeingViewed, setArchiveBeingViewed] = useState<number | undefined>();
  const [archiveBeingRemoved, setArchiveBeingRemoved] = useState<number | undefined>();

  const renderArchiveRow = (archive: Archive) => {
    const isViewing = archiveBeingViewed === archive.id;
    const isRemoving = archiveBeingRemoved === archive.id && loadingRemoveArchive;
    const canRemove = !!onRemoveArchive && !openedFromView && !viewOnly;

    return (
      <Stack key={archive.id} direction="row" alignItems="center" className={classes.archiveRow}>
        <VscFilePdf size={16} color={colors.error500} className={classes.archiveFileIcon} />
        <span className={classes.archiveFilename} title={archive.filename}>
          {archive.filename}
        </span>
        <Stack direction="row" alignItems="center" className={classes.archiveActions}>
          {isViewing ? (
            <CircularProgress size={16} className={classes.archiveActionLoading} />
          ) : (
            <DefaultTooltip title={translate("viewFile")}>
              <button
                type="button"
                className={classes.archiveIconButton}
                onClick={() => {
                  setArchiveBeingViewed(archive.id);
                  downloadDocumentAndViewFile(archive.uri, () => setArchiveBeingViewed(undefined));
                }}
                aria-label={translate("viewFile")}
              >
                <FiEye size={16} color={colors.brandBlue500} />
              </button>
            </DefaultTooltip>
          )}
          {canRemove ? (
            isRemoving ? (
              <CircularProgress size={16} className={classes.archiveActionLoading} />
            ) : (
              <DefaultTooltip title={translate("delete")}>
                <button
                  type="button"
                  className={classes.archiveIconButton}
                  onClick={() => {
                    setArchiveBeingRemoved(archive.id);
                    onRemoveArchive?.(archive.id);
                  }}
                  aria-label={translate("delete")}
                  disabled={loadingRemoveArchive}
                >
                  <FiTrash2 size={16} color={colors.error500} />
                </button>
              </DefaultTooltip>
            )
          ) : null}
        </Stack>
      </Stack>
    );
  };

  const renderArchivesList = () => (
    <Stack direction="column" className={classes.archivesList}>
      {archives.map(renderArchiveRow)}
    </Stack>
  );

  const renderBottomInfo = () => {
    if (isThereAFile) {
      if (archives.length > 0) {
        return renderArchivesList();
      }

      return (
        <BottomButtonContainer>
          <TextButton
            {...buttons.viewFile}
            onClick={() => downloadDocumentAndViewFile(shipmentDocument.file ?? "")}
            disabled={loadingViewFile}
            loading={loadingViewFile}
          />
          {shipmentDocument.generated && isCiplDocument && onDownloadDocumentCsv ? (
            <TextButton
              text={translate("downloadCsv")}
              renderIcon={(props?: IconBaseProps) => <FiDownload {...props} />}
              textLoading={`${translate("generatingFile")}...`}
              onClick={() => {
                setLoadingCsvFile(true);
                onDownloadDocumentCsv(shipmentDocument.id ?? 0, shipmentDocument.reference, () =>
                  setLoadingCsvFile(false)
                );
              }}
              disabled={loadingCsvFile}
              loading={loadingCsvFile}
            />
          ) : null}
          {canFileBeCleared ? (
            <TextButton
              {...buttons.clearFile}
              onClick={() => onClearDocument(shipmentDocument)}
              disabled={loadingClearDocument}
              loading={loadingClearDocument}
            />
          ) : null}
        </BottomButtonContainer>
      );
    }

    return <BottomButtonContainer>{renderButtonUpload()}</BottomButtonContainer>;
  };

  const renderUpdateDateTimeInfo = () => {
    const updateDate = shipmentDocument?.uploadedAt;
    const isUploaded = shipmentDocument?.uploaded;

    if (!updateDate || !isUploaded) return;

    return (
      <Stack flexDirection="row">
        <Stack>
          <span className={classes.dueText}>{`${translate("uploadDateAndTime")}`}</span>
          <span className={classes.bottomText}>
            {format(new Date(updateDate), "dd/MM/yyyy hh:mm aaaaa'm'")
              .toUpperCase()
              .replace(" ", " • ")}
          </span>
        </Stack>
      </Stack>
    );
  };

  return (
    <Grid item xs={12} sm={12} md={6}>
      <div className={classes.container}>
        <Stack direction={"row"} justifyContent={"space-between"} className={classes.topText}>
          <span>{shipmentDocument?.visualName || shipmentDocument?.name}</span>
          {canFileBeDeleted ? (
            <DeleteDocumentIcon
              id="deleteDocumentIcon"
              color={colors.error500}
              size={20}
              onClick={() =>
                deleteModalRef.current?.open(shipmentDocument.id ?? -1, shipmentDocument.name)
              }
            />
          ) : null}
          <Stack
            direction={"row"}
            spacing={2}
            justifyContent={"center"}
            alignItems={"center"}
            className={`${classes.uploadText}, ${classes.documentItemBadge}, ${
              canFileBeDeleted ? "deletableDocumentItemBadge" : ""
            }`}
          >
            {renderTopInfo()}
          </Stack>
        </Stack>
        <Stack
          className={classes.bottom}
          direction={archives.length > 0 ? "column" : "row"}
          spacing={archives.length > 0 ? 1.5 : 0}
          justifyContent={"flex-start"}
        >
          {renderUpdateDateTimeInfo()}
          {renderBottomInfo()}
        </Stack>
      </div>
      <DeleteDocumentModal
        ref={deleteModalRef}
        onSubmit={() => onDeleteDocument(shipmentDocument.id ?? 0)}
      />
    </Grid>
  );
};

export default DocumentsItem;
